/* eslint-disable no-undef */
class ProductGalleryElem extends HTMLElement {
  constructor() {
    super();
    this.isMobile = window.innerWidth < 1024;
    this.videoPlaying = null;
    this.activeMobileVideoSlide = null;
    this.featuredVideoPlaying = false;
    this.boundHandleVideoPlayPause = this.handleVideoPlayPause.bind(this);
  }

  connectedCallback() {
    this.featuredImage = this.querySelector(".js-featured-gallery-image");
    this.featuredVideoContainer = this.querySelector(".js-featured-product-video");
    this.featuredContainer = this.querySelector(".product-gallery__img");

    this.addEventListener("click", this.handleImageClick);
    this.addEventListener("click", this.handleVideoClick);

    // Listen for window resize events to handle responsive behavior
    window.addEventListener('resize', this.handleResize);
    
    // Add event listeners to track when videos start/stop playing
    this.setupVideoEventListeners();
    
    // Add direct event listeners to all SVG elements and play buttons
    this.setupSVGClickListeners();
    
    // Add clickable overlays for iframe thumbnails
    this.setupIframeOverlays();
    
    // Setup mutation observer to handle dynamically added videos
    this.setupMutationObserver();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleImageClick);
    this.removeEventListener("click", this.handleVideoClick);
    window.removeEventListener('resize', this.handleResize);
    
    // Remove video event listeners
    this.removeVideoEventListeners();
    
    // Remove SVG click listeners
    this.removeSVGClickListeners();
    
    // Disconnect mutation observer
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  
  setupMutationObserver() {
    // Create a mutation observer to watch for dynamically added videos and play buttons
    this.observer = new MutationObserver((mutations) => {
      let needsSetup = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            // Check if the added node is a video, iframe, or contains these elements
            if (node.nodeName === 'VIDEO' || node.nodeName === 'IFRAME' || 
                (node.nodeType === 1 && (
                  node.querySelector('video') || 
                  node.querySelector('iframe') || 
                  node.querySelector('.product-gallery__play-overlay') ||
                  node.querySelector('.js-video-trigger')
                ))) {
              needsSetup = true;
            }
          });
        }
      });
      
      // If new videos or play buttons were added, refresh our event listeners
      if (needsSetup) {
        this.setupVideoEventListeners();
        this.setupSVGClickListeners();
        this.setupIframeOverlays();
      }
    });
    
    // Start observing the entire gallery element for changes
    this.observer.observe(this, { childList: true, subtree: true });
  }
  
  setupSVGClickListeners() {
    // Remove existing listeners first to prevent duplicates
    this.removeSVGClickListeners();
    
    // Get all SVG elements with the js-video-trigger class
    const svgTriggers = this.querySelectorAll('svg.js-video-trigger, path.js-video-trigger, circle.js-video-trigger');
    svgTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleSVGClick);
    });
    
    // Get all play overlay buttons
    const playButtons = this.querySelectorAll('.product-gallery__play-overlay');
    playButtons.forEach(button => {
      button.addEventListener('click', this.handleSVGClick);
    });
  }
  
  removeSVGClickListeners() {
    // Get all SVG elements with the js-video-trigger class
    const svgTriggers = this.querySelectorAll('svg.js-video-trigger, path.js-video-trigger, circle.js-video-trigger');
    svgTriggers.forEach(trigger => {
      trigger.removeEventListener('click', this.handleSVGClick);
    });
    
    // Get all play overlay buttons
    const playButtons = this.querySelectorAll('.product-gallery__play-overlay');
    playButtons.forEach(button => {
      button.removeEventListener('click', this.handleSVGClick);
    });
  }
  
  setupIframeOverlays() {
    // Find all iframe thumbnails
    const iframeThumbnails = this.querySelectorAll('.product-gallery__slide-link[data-media-type="external_video"]');
    
    iframeThumbnails.forEach(thumbnail => {
      // Check if overlay already exists
      if (!thumbnail.querySelector('.product-gallery__iframe-overlay')) {
        // Create overlay element
        const overlay = document.createElement('div');
        overlay.classList.add('product-gallery__iframe-overlay');
        overlay.setAttribute('data-media-id', thumbnail.getAttribute('data-media-id'));
        overlay.setAttribute('data-media-type', 'external_video');
        
        // Add click handler directly to the overlay
        overlay.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          
          // Get media data
          const mediaId = thumbnail.dataset.mediaId;
          const mediaType = thumbnail.dataset.mediaType;
          
          
          if (this.isMobile) {
            // For mobile, handle thumbnail video
            this.handleMobileThumbnailVideo(thumbnail, mediaType, thumbnail.querySelector('iframe'));
          } else {
            // For desktop, load in featured area
            
            // Update active class
            const currentlyActive = this.querySelector(".product-gallery__slide-link--active");
            if (currentlyActive) {
              currentlyActive.classList.remove("product-gallery__slide-link--active");
            }
            thumbnail.classList.add("product-gallery__slide-link--active");
            
            // Load the video into the featured area
            this.loadFeaturedMedia(mediaId, mediaType);
          }
        });
        
        // Append overlay to thumbnail
        thumbnail.appendChild(overlay);
      }
    });
  }
  
  handleSVGClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    
    // Determine if this is for the featured video or a thumbnail
    const isFeaturedOverlay = event.target.closest('.product-gallery__play-overlay--featured');
    
    if (isFeaturedOverlay) {
      // Handle featured video play
      const video = this.querySelector('.js-featured-product-video');
      if (video && video.tagName === 'VIDEO') {
        video.play()
          .then(() => {
            isFeaturedOverlay.style.opacity = '0';
            isFeaturedOverlay.style.visibility = 'hidden';
            isFeaturedOverlay.classList.add('play-button-hidden');
          })
          .catch(e => {
            console.log("Failed to play featured video:", e);
          });
      }
    } else {
      // For thumbnails
      const slideLink = event.target.closest('.js-product-gallery__slide-link');
      if (slideLink) {
        const mediaType = slideLink.dataset.mediaType;
        const mediaId = slideLink.dataset.mediaId;
        
        if (this.isMobile && (mediaType === 'video' || mediaType === 'external_video')) {
          // For mobile we play directly in the thumbnail
          const videoElement = slideLink.querySelector('video');
          const slideElement = slideLink.closest('.product-gallery__slide');
          
          // First pause any other playing videos
          this.pauseAllVideos();
          
          // Show all play buttons
          this.showAllPlayButtons();
          
          if (videoElement && videoElement.tagName === 'VIDEO') {
            // Enable controls and play the video
            videoElement.controls = true;
            videoElement.muted = true;
            videoElement.play()
              .then(() => {
                // If play succeeds
                this.videoPlaying = videoElement;
                slideElement.classList.add('has-active-video');
                this.activeMobileVideoSlide = slideElement;
                
                // Hide this specific play button
                const playButton = slideElement.querySelector('.product-gallery__play-overlay');
                if (playButton) {
                  playButton.style.opacity = '0';
                  playButton.style.visibility = 'hidden';
                  playButton.classList.add('play-button-hidden');
                }
              })
              .catch(e => {
                console.log("Video play failed:", e);
              });
          } else if (mediaType === 'external_video') {
            // Handle YouTube/Vimeo videos
            const iframe = slideLink.querySelector('iframe');
            if (iframe) {
              try {
                iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                iframe.contentWindow.postMessage('{"method":"play"}', '*');
                
                slideElement.classList.add('has-active-video');
                this.activeMobileVideoSlide = slideElement;
                
                // Hide this specific play button
                const playButton = slideElement.querySelector('.product-gallery__play-overlay');
                if (playButton) {
                  playButton.style.opacity = '0';
                  playButton.style.visibility = 'hidden';
                  playButton.classList.add('play-button-hidden');
                }
              } catch (e) {
                console.log("Failed to play external video:", e);
              }
            }
          }
          
          // Update active class
          this.updateActiveSlide(slideElement);
        } else {
          // For desktop we load in featured area
          this.pauseAllVideos();
          this.loadFeaturedMedia(mediaId, mediaType);
          
          // Update active class
          const slideElement = slideLink.closest('.product-gallery__slide');
          this.updateActiveSlide(slideElement);
        }
      }
    }
  }
  
  pauseAllVideos() {
    // Pause all videos
    const videos = this.querySelectorAll("video");
    videos.forEach(video => {
      if (video.pause) {
        video.pause();
        video.controls = false;
        video.muted = true; // Ensure videos are muted when paused
      }
    });
    
    // Reset state
    this.videoPlaying = null;
    
    // Reset active video class on all slides except the current one
    const activeVideoSlides = this.querySelectorAll('.has-active-video');
    activeVideoSlides.forEach(slide => {
      slide.classList.remove('has-active-video');
    });
  }
  
  showAllPlayButtons() {
    // Show all play buttons
    const playButtons = this.querySelectorAll('.product-gallery__play-overlay');
    playButtons.forEach(button => {
      button.style.opacity = '1';
      button.style.visibility = 'visible';
      button.classList.remove('play-button-hidden');
    });
  }
  
  updateActiveSlide(slideElement) {
    // Update active class for visual indication
    const currentlyActive = this.querySelector(".product-gallery__slide-link--active");
    if (currentlyActive) {
      currentlyActive.classList.remove("product-gallery__slide-link--active");
    }
    if (slideElement) {
      const slideLink = slideElement.querySelector('.js-product-gallery__slide-link');
      if (slideLink) {
        slideLink.classList.add("product-gallery__slide-link--active");
      } else {
        slideElement.classList.add("product-gallery__slide-link--active");
      }
    }
  }
  
  setupVideoEventListeners() {
    // Remove existing listeners first to prevent duplicates
    this.removeVideoEventListeners();
    
    // Find all videos in the gallery
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      // Use the bound event handler
      video.addEventListener('play', this.boundHandleVideoPlayPause);
      video.addEventListener('pause', this.boundHandleVideoPlayPause);
    });
    
    // Also add listeners to the featured video if it exists
    const featuredVideo = this.querySelector('.js-featured-product-video');
    if (featuredVideo && featuredVideo.tagName === 'VIDEO') {
      featuredVideo.addEventListener('play', this.handleFeaturedVideoPlay);
      featuredVideo.addEventListener('pause', this.handleFeaturedVideoPause);
    }
  }
  
  removeVideoEventListeners() {
    // Find all videos in the gallery
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      // Use the bound event handler
      video.removeEventListener('play', this.boundHandleVideoPlayPause);
      video.removeEventListener('pause', this.boundHandleVideoPlayPause);
    });
    
    // Also remove listeners from the featured video if it exists
    const featuredVideo = this.querySelector('.js-featured-product-video');
    if (featuredVideo && featuredVideo.tagName === 'VIDEO') {
      featuredVideo.removeEventListener('play', this.handleFeaturedVideoPlay);
      featuredVideo.removeEventListener('pause', this.handleFeaturedVideoPause);
    }
  }
  
  // Unified handler for both play and pause events
  handleVideoPlayPause(event) {
    // Check if this is a play or pause event
    const isPlay = event.type === 'play';
    
    if (event.target.classList.contains('js-product-video')) {
      // Update our tracking of which video is playing
      this.videoPlaying = isPlay ? event.target : null;
      
      // Find the play button associated with this video
      const slideLink = event.target.closest('.js-product-gallery__slide-link');
      if (slideLink) {
        const playButton = slideLink.querySelector('.product-gallery__play-overlay');
        if (playButton) {
          if (isPlay) {
            // Hide play button when video is playing
            playButton.style.opacity = '0';
            playButton.style.visibility = 'hidden';
            playButton.classList.add('play-button-hidden');
            
            // Add active class to the slide
            const slideElement = slideLink.closest('.product-gallery__slide');
            if (slideElement) {
              slideElement.classList.add('has-active-video');
              this.activeMobileVideoSlide = slideElement;
            }
          } else {
            // Show play button when video is paused
            playButton.style.opacity = '1';
            playButton.style.visibility = 'visible';
            playButton.classList.remove('play-button-hidden');
            
            // Remove active class from the slide
            const slideElement = slideLink.closest('.product-gallery__slide');
            if (slideElement) {
              slideElement.classList.remove('has-active-video');
              if (this.activeMobileVideoSlide === slideElement) {
                this.activeMobileVideoSlide = null;
              }
            }
          }
        }
      }
    }
  }
  
  handleFeaturedVideoPlay = () => {
    this.featuredVideoPlaying = true;
    // Hide featured play button
    const playButton = this.querySelector('.product-gallery__play-overlay--featured');
    if (playButton) {
      playButton.style.opacity = '0';
      playButton.style.visibility = 'hidden';
      playButton.classList.add('play-button-hidden');
    }
  }
  
  handleFeaturedVideoPause = () => {
    this.featuredVideoPlaying = false;
    // Show featured play button
    const playButton = this.querySelector('.product-gallery__play-overlay--featured');
    if (playButton) {
      playButton.style.opacity = '1';
      playButton.style.visibility = 'visible';
      playButton.classList.remove('play-button-hidden');
    }
  }

  handleResize = () => {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 1024;
    
    // When entering mobile view (desktop to mobile), make zoom icon visible
    if (this.isMobile) {
      const mobileZoomIcon = this.querySelector('.product-gallery__zoom-icon.product-gallery__zoom-icon--featured')
      if (mobileZoomIcon) {
        mobileZoomIcon.style.display = "flex";
      }
    }

    // Going from mobile to desktop
    if (wasMobile && !this.isMobile) {
      // Pause any playing video in thumbnails
      this.resetMobileVideoThumbnails();
      
      // Stop any playing iframe videos in thumbnails
      this.stopAllIframeVideos();
      
      // Find and click the first thumbnail with an image
      const firstSlideLink = this.querySelector('.product-gallery__slide .js-product-gallery__slide-link img');
      if (firstSlideLink) {
        // Click the image to trigger image swap
        firstSlideLink.click();
      }
    } 
    // Going from desktop to mobile
    else if (!wasMobile && this.isMobile) {
      // Pause any featured video if it's playing
      const featuredVideo = this.querySelector('.js-featured-product-video');
      if (featuredVideo) {
        if (featuredVideo.tagName === 'VIDEO' && !featuredVideo.paused) {
          featuredVideo.pause();
          
          // Show the play button for the featured video
          const playButton = this.querySelector('.product-gallery__play-overlay--featured');
          if (playButton) {
            playButton.style.opacity = '1';
            playButton.style.visibility = 'visible'; 
            playButton.classList.remove('play-button-hidden');
          }
        } else if (featuredVideo.tagName === 'IFRAME') {
          // Stop iframe video
          this.stopIframeVideo(featuredVideo);
        }
      }
      
      // If a featured video was playing, find its thumbnail and mark it as active
      if (this.featuredVideoPlaying) {
        const activeSlide = this.querySelector('.product-gallery__slide-link--active');
        if (activeSlide) {
          const slideElement = activeSlide.closest('.product-gallery__slide');
          if (slideElement) {
            slideElement.classList.add('has-active-video');
            this.activeMobileVideoSlide = slideElement;
          }
        }
      }
    }
    
    // Ensure our event listeners are set up correctly after resize
    this.setupVideoEventListeners();
    this.setupSVGClickListeners();
  }

  resetMobileVideoThumbnails() {
    // Find all video thumbnails
    const videoThumbnails = this.querySelectorAll(".js-product-gallery__slide-link[data-media-type='video'], .js-product-gallery__slide-link[data-media-type='external_video']");
    videoThumbnails.forEach(thumbnail => {
      const video = thumbnail.querySelector('video');
      if (video) {
        // Pause the video
        video.pause();
        // Make sure controls are hidden
        video.controls = false;
      }
      
      // Reset any play overlay opacity
      const playOverlay = thumbnail.querySelector('.product-gallery__play-overlay');
      if (playOverlay) {
        playOverlay.style.opacity = '1';
        playOverlay.style.visibility = 'visible';
        playOverlay.classList.remove('play-button-hidden');
      }
      
      // Remove active video class
      thumbnail.closest('.product-gallery__slide').classList.remove('has-active-video');
    });

    // Reset the video playing reference
    this.videoPlaying = null;
    this.activeMobileVideoSlide = null;
  }

  handleImageClick = (event) => {
    if (event.target.classList.contains("js-gallery-image") && !event.target.classList.contains("js-product-video")) {
      this.swapImage(event.target);
    }
  };

  handleVideoClick = (event) => {
    // Check if we clicked on any element with the js-video-trigger class
    // This could be the container, SVG, circle, or path
    const isVideoTrigger = event.target.classList.contains("js-video-trigger") || 
                           event.target.closest(".js-video-trigger");
    
    // Handle clicks on video elements, thumbnails or play buttons
    const isVideoElement = event.target.classList.contains("js-product-video");
    const isVideoThumbnail = !isVideoElement && 
                            (event.target.closest('.js-product-gallery__slide-link[data-media-type="video"]') || 
                             event.target.closest('.js-product-gallery__slide-link[data-media-type="external_video"]'));
    
    // Check if we clicked on iframe overlay
    const isIframeOverlay = event.target.classList.contains('product-gallery__iframe-overlay');
    
    if (isVideoElement || isVideoTrigger || isVideoThumbnail || isIframeOverlay) {
      event.preventDefault();
      event.stopPropagation();
      
      
      // If this is an iframe overlay, it has its own handler
      if (isIframeOverlay) {
        return; // The click is already handled by the overlay's event listener
      }
      
      // Find the related video element
      let videoElement = null;
      
      if (isVideoElement) {
        videoElement = event.target;
      } else if (isVideoTrigger) {
        // Find the closest play overlay to determine if this is a featured video
        const playOverlay = event.target.closest('.product-gallery__play-overlay') || 
                            event.target.querySelector('.product-gallery__play-overlay');
        
        if (playOverlay && playOverlay.classList.contains('product-gallery__play-overlay--featured')) {
          // This is for the featured video
          videoElement = this.querySelector(".js-featured-product-video");
        } else {
          // For thumbnail videos, find the containing button
          const button = event.target.closest(".js-product-gallery__slide-link");
          if (button) {
            videoElement = button.querySelector(".js-product-video") || button.querySelector("video");
          }
        }
      } else if (isVideoThumbnail) {
        // Direct click on a video thumbnail
        const button = event.target.closest(".js-product-gallery__slide-link");
        if (button) {
          videoElement = button.querySelector(".js-product-video") || button.querySelector("video");
        }
      }
      
      // Get the button (container) for the video
      let button = null;
      
      if (videoElement) {
        button = videoElement.closest(".js-product-gallery__slide-link");
      } else if (isVideoTrigger || isVideoThumbnail) {
        button = event.target.closest(".js-product-gallery__slide-link");
      }
      
      // Check if we're dealing with the featured video
      const isFeaturedVideo = (videoElement && videoElement.classList.contains("js-featured-product-video")) ||
                             (isVideoTrigger && event.target.closest(".product-gallery__play-overlay--featured"));
      
      // For featured video, just toggle play/pause
      if (isFeaturedVideo) {
        const video = this.querySelector(".js-featured-product-video");
        if (video && video.tagName === 'VIDEO') {
          if (video.paused) {
            // Unmute only when we're explicitly playing
            video.muted = false;
            video.play()
              .then(() => {
                // Show controls and unmute when playing
                video.controls = true;
                // Hide the play button when playing
                const playButton = this.querySelector(".product-gallery__play-overlay--featured");
                if (playButton) {
                  playButton.style.opacity = '0';
                  playButton.style.visibility = 'hidden';
                  playButton.classList.add('play-button-hidden');
                }
              })
              .catch(e => {
                console.log("Failed to play featured video:", e);
                // If autoplay fails, ensure video is muted
                video.muted = true;
              });
          } else {
            video.pause();
            // Mute and hide controls when paused
            video.muted = true;
            video.controls = false;
            // Show the play button when paused
            const playButton = this.querySelector(".product-gallery__play-overlay--featured");
            if (playButton) {
              playButton.style.opacity = '1';
              playButton.style.visibility = 'visible';
              playButton.classList.remove('play-button-hidden');
            }
          }
        } else if (video && video.tagName === 'IFRAME') {
          // For featured iframe videos, we don't have a play/pause toggle in the same way
          // YouTube and Vimeo handle this internally
        }
        return;
      }
      
      // If we don't have a button, we can't proceed
      if (!button) {
        return;
      }
      
      // Get media data
      const mediaId = button.dataset.mediaId;
      const mediaType = button.dataset.mediaType;
      
      
      // Mobile-specific behavior for thumbnail videos
      if (this.isMobile && (mediaType === 'video' || mediaType === 'external_video')) {
        this.handleMobileThumbnailVideo(button, mediaType, videoElement);
        return;
      }
      
      // Desktop behavior - update featured area
      
      // Find all video elements in the gallery and pause them
      const videos = this.querySelectorAll("video");
      videos.forEach(video => {
        if (video.pause) {
          video.pause();
          video.muted = true; // Ensure all videos are muted when paused
        }
      });
      
      // Update active class
      const currentlyActive = this.querySelector(".product-gallery__slide-link--active");
      if (currentlyActive) {
        currentlyActive.classList.remove("product-gallery__slide-link--active");
      }
      button.classList.add("product-gallery__slide-link--active");
      
      // Load the video into the featured area
      this.loadFeaturedMedia(mediaId, mediaType);
    }
  };

  handleMobileThumbnailVideo(button, mediaType, videoElement) {
    // First pause any currently playing video if it's different from the current one
    if (this.videoPlaying && this.videoPlaying !== videoElement) {
      this.videoPlaying.pause();
      // Make sure controls are hidden when paused
      this.videoPlaying.controls = false;
      
      // Show play button for previously playing video
      const previousPlayButton = this.videoPlaying.nextElementSibling;
      if (previousPlayButton && previousPlayButton.classList.contains('product-gallery__play-overlay')) {
        previousPlayButton.style.opacity = '1';
        previousPlayButton.style.visibility = 'visible';
        previousPlayButton.classList.remove('play-button-hidden');
      }
      
      // Remove active video class from previous slide
      if (this.activeMobileVideoSlide) {
        this.activeMobileVideoSlide.classList.remove('has-active-video');
      }
    }
    
    const slideElement = button.closest('.product-gallery__slide');
    
    // If we don't have a video element yet but we have a button, try to find the video
    if (!videoElement && button) {
      videoElement = button.querySelector('video') || button.querySelector('iframe');
    }
    
    // Toggle play/pause for videos
    if (videoElement && videoElement.tagName === 'VIDEO') {
      // Toggle play/pause on the clicked video
      if (videoElement.paused) {
        // Enable controls for better mobile interaction
        videoElement.controls = true;
        videoElement.muted = true; // Unmute for better UX
        
        // Play the video
        videoElement.play()
          .then(() => {
            // If play succeeds
            this.videoPlaying = videoElement;
            slideElement.classList.add('has-active-video');
            this.activeMobileVideoSlide = slideElement;
            
            // Hide play button
            const playButton = slideElement.querySelector('.product-gallery__play-overlay') || 
                              button.querySelector('.product-gallery__play-overlay');
            if (playButton) {
              playButton.style.opacity = '0';
              playButton.style.visibility = 'hidden';
              
              // Add a more direct class for hiding
              playButton.classList.add('play-button-hidden');
            }
          })
          .catch(e => {
            // If autoplay fails, show the play button again
            const playButton = slideElement.querySelector('.product-gallery__play-overlay') || 
                              button.querySelector('.product-gallery__play-overlay');
            if (playButton) {
              playButton.style.opacity = '1';
              playButton.style.visibility = 'visible';
              playButton.classList.remove('play-button-hidden');
            }
          });
      } else {
        // Pause the video
        videoElement.pause();
        this.videoPlaying = null;
        this.activeMobileVideoSlide = null;
        slideElement.classList.remove('has-active-video');
        
        // Hide controls when paused
        videoElement.controls = false;
        
        // Show play button
        const playButton = slideElement.querySelector('.product-gallery__play-overlay') || 
                          button.querySelector('.product-gallery__play-overlay');
        if (playButton) {
          playButton.style.opacity = '1';
          playButton.style.visibility = 'visible';
          playButton.classList.remove('play-button-hidden');
        }
      }
    } else if (mediaType === 'external_video') {
      // Handle external videos (YouTube/Vimeo)
      const iframe = button.querySelector('iframe');
      if (iframe) {
        // Try to play the video via postMessage
        try {
          iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          iframe.contentWindow.postMessage('{"method":"play"}', '*');
          
          // Mark this slide as having an active video
          slideElement.classList.add('has-active-video');
          this.activeMobileVideoSlide = slideElement;
          
          // Hide play button
          const playButton = slideElement.querySelector('.product-gallery__play-overlay') || 
                            button.querySelector('.product-gallery__play-overlay');
          if (playButton) {
            playButton.style.opacity = '0';
            playButton.style.visibility = 'hidden';
            playButton.classList.add('play-button-hidden');
          }
        } catch (e) {
          console.log("Failed to play external video:", e);
        }
      }
    }
    
    // Update active class for visual indication
    const currentlyActive = this.querySelector(".product-gallery__slide-link--active");
    if (currentlyActive) {
      currentlyActive.classList.remove("product-gallery__slide-link--active");
    }
    button.classList.add("product-gallery__slide-link--active");
  }

  loadFeaturedMedia(mediaId, mediaType) {
    if (!this.featuredContainer) return;
    
    
    // Clear the container
    this.featuredContainer.innerHTML = '';
    
    // Hide or show zoom icon based on media type
    const zoomIcon = this.querySelector('.product-gallery__zoom-icon--featured');
    if (zoomIcon) {
      if (mediaType === 'video' || mediaType === 'external_video') {
        zoomIcon.style.display = 'none';
      } else {
        zoomIcon.style.display = 'flex';
      }
    }
    
    if (mediaType === 'video') {
      // Get the original video element
      const originalVideoContainer = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!originalVideoContainer) return;
      
      // Get the video element or create a clone
      const originalVideo = originalVideoContainer.querySelector('video');
      if (!originalVideo) return;
      
      // We need to create a new video element and copy all sources
      const video = document.createElement('video');
      video.classList.add('image--contain', 'js-featured-gallery-image', 'js-featured-product-video');
      video.controls = false; // Start without controls
      video.autoplay = false;
      video.loop = true;
      video.muted = true; // Start muted by default
      video.style.objectFit = 'cover'; // Ensure video fills container
      video.style.width = '100%';
      video.style.height = '100%';
      video.playsinline = true; // Better for mobile
      
      // Copy all source elements from the original video
      const sources = originalVideo.querySelectorAll('source');
      if (sources.length > 0) {
        sources.forEach(source => {
          const newSource = document.createElement('source');
          newSource.src = source.src;
          newSource.type = source.type;
          video.appendChild(newSource);
        });
      } else if (originalVideo.src) {
        // If no source elements, try to use the src attribute directly
        video.src = originalVideo.src;
      }
      
      this.featuredContainer.appendChild(video);
      
      // Add play overlay with js-video-trigger on all elements
      const playOverlay = document.createElement('div');
      playOverlay.classList.add('product-gallery__play-overlay', 'product-gallery__play-overlay--featured', 'js-video-trigger');
      playOverlay.innerHTML = `
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="js-video-trigger">
          <circle cx="24" cy="24" r="24" fill="rgba(0, 35, 65, 0.7)" class="js-video-trigger"/>
          <path d="M32 24L20 32V16L32 24Z" fill="white" class="js-video-trigger"/>
        </svg>
      `;
      this.featuredContainer.appendChild(playOverlay);
      
      // Update the featured image reference
      this.featuredImage = video;
      
      // Set up event listeners for the video and play button
      video.addEventListener('play', () => {
        this.featuredVideoPlaying = true;
        // Show controls when playing
        video.controls = true;
        // Keep video muted when playing in featured area
        playOverlay.style.opacity = '0';
        playOverlay.style.visibility = 'hidden';
        playOverlay.classList.add('play-button-hidden');
      });
      
      video.addEventListener('pause', () => {
        this.featuredVideoPlaying = false;
        // Hide controls when paused
        video.controls = false;
        // Mute when paused
        video.muted = true;
        playOverlay.style.opacity = '1';
        playOverlay.style.visibility = 'visible';
        playOverlay.classList.remove('play-button-hidden');
      });
      
      video.addEventListener('click', () => {
        if (video.paused) {
          video.controls = true;
          // Keep video muted when playing in featured area
          video.play();
        } else {
          video.pause();
          video.controls = false;
          video.muted = true;
        }
      });
      
      playOverlay.addEventListener('click', () => {
        video.controls = true;
        // Keep video muted when playing in featured area
        video.play();
      });
      
      // Try to play the video after a short delay
      setTimeout(() => {
        video.play().catch(e => {
          // Ensure play button is visible if autoplay fails
          playOverlay.style.opacity = '1';
          playOverlay.style.visibility = 'visible';
          playOverlay.classList.remove('play-button-hidden');
        });
      }, 100);
    } 
    else if (mediaType === 'external_video') {
      const originalContainer = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!originalContainer) return;
      
      const originalIframe = originalContainer.querySelector('iframe');
      if (!originalIframe) return;
      
      // Create a new iframe
      const iframe = document.createElement('iframe');
      
      // Ensure the iframe source has mute=1 parameter
      let iframeSrc = originalIframe.src;
      
      // Force mute parameter for YouTube videos
      if (iframeSrc.includes('youtube.com')) {
        if (!iframeSrc.includes('mute=1')) {
          iframeSrc = iframeSrc.includes('?') 
            ? `${iframeSrc}&mute=1` 
            : `${iframeSrc}?mute=1`;
        }
      } 
      // Force mute parameter for Vimeo videos
      else if (iframeSrc.includes('vimeo.com')) {
        if (!iframeSrc.includes('muted=1')) {
          iframeSrc = iframeSrc.includes('?') 
            ? `${iframeSrc}&muted=1` 
            : `${iframeSrc}?muted=1`;
        }
      }
      
      iframe.src = iframeSrc;
      iframe.classList.add('image--contain', 'js-featured-gallery-image', 'js-featured-product-video');
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.objectFit = 'cover';
      iframe.frameBorder = 0;
      iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
      iframe.allowFullscreen = true;
      
      this.featuredContainer.appendChild(iframe);
      
      // Don't add play overlay for iframes - they have their own controls
      
      // Update the featured image reference
      this.featuredImage = iframe;
      
      // Mark as playing (even though we don't have a play button to click)
      this.featuredVideoPlaying = true;
    }
  }

  swapImage(clickedImage) {
    if (this.featuredImage && clickedImage) {
      // Pause any playing videos before swapping to an image
      const videos = this.querySelectorAll("video");
      videos.forEach(video => {
        if (video.pause) video.pause();
      });
      
      // Reset featured video playing flag
      this.featuredVideoPlaying = false;
      
      // Remove active video class from any slides
      if (this.activeMobileVideoSlide) {
        this.activeMobileVideoSlide.classList.remove('has-active-video');
        this.activeMobileVideoSlide = null;
      }
      
      // Show zoom icon for images
      const zoomIcon = this.querySelector('.product-gallery__zoom-icon--featured');
      if (zoomIcon) {
        zoomIcon.style.display = 'flex';
      }
      
      // Clear the featured container and add the new image
      if (this.featuredContainer) {
        this.featuredContainer.innerHTML = '';
        
        const img = document.createElement('img');
        img.src = clickedImage.src;
        img.srcset = clickedImage.srcset || '';
        img.alt = clickedImage.alt || "";
        img.classList.add('image--contain', 'js-featured-gallery-image');
        img.width = 1000;
        img.height = 963;
        
        this.featuredContainer.appendChild(img);
        this.featuredImage = img;
      }

      // Update active class
      const currentlyActive = this.querySelector(".product-gallery__slide-link--active");
      if (currentlyActive) {
        currentlyActive.classList.remove("product-gallery__slide-link--active");
      }
      
      // Find and activate the parent slide link
      const slideLink = clickedImage.closest(".product-gallery__slide-link");
      if (slideLink) {
        slideLink.classList.add("product-gallery__slide-link--active");
      } else {
        clickedImage.closest(".product-gallery__slide").classList.add("product-gallery__slide-link--active");
      }
    }
  }

  // Helper method to stop all iframe videos
  stopAllIframeVideos() {
    const iframes = this.querySelectorAll('iframe');
    iframes.forEach(iframe => {
      this.stopIframeVideo(iframe);
    });
  }
  
  // Helper method to stop a specific iframe video
  stopIframeVideo(iframe) {
    if (!iframe) return;
    
    try {
      // For YouTube
      iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      // For Vimeo
      iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      
      // Reload iframe to fully stop the video
      const src = iframe.src;
      iframe.src = src;
    } catch (e) {
      console.log("Failed to stop iframe video:", e);
    }
  }
}

customElements.define("product-gallery", ProductGalleryElem);
